<script setup>
import { ref, watch, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import CaptainAnalyticsAPI from 'dashboard/api/captain/analytics';
import Spinner from 'shared/components/Spinner.vue';

const props = defineProps({
  filters: {
    type: Object,
    default: () => ({}),
  },
});
const route = useRoute();
const router = useRouter();

const insights = ref([]);
const meta = ref({ current_page: 1, total_pages: 1, total_count: 0 });
const isLoading = ref(false);

const fetchInsights = async (page = 1) => {
  isLoading.value = true;
  try {
    const response = await CaptainAnalyticsAPI.getInsights({
      inboxId: props.filters.inbox_id,
      startDate: props.filters.start_date,
      endDate: props.filters.end_date,
      page,
      perPage: 10,
    });
    insights.value = response.data.insights;
    meta.value = response.data.meta;
  } catch {
    // Error handled silently - data will remain empty
  } finally {
    isLoading.value = false;
  }
};

watch(
  () => props.filters,
  () => fetchInsights(1),
  { deep: true, immediate: true }
);

const getSentimentClass = sentiment => {
  const classes = {
    positive: 'bg-n-green-3 text-n-green-11',
    neutral: 'bg-n-slate-3 text-n-slate-11',
    negative: 'bg-n-ruby-3 text-n-ruby-11',
  };
  return classes[sentiment] || classes.neutral;
};

const getPendingClass = pendingBy => {
  const classes = {
    agent: 'bg-n-amber-3 text-n-amber-11',
    customer: 'bg-n-blue-3 text-n-blue-11',
    none: 'bg-n-green-3 text-n-green-11',
  };
  return classes[pendingBy] || classes.none;
};

const formatHours = hours => {
  if (!hours) return '-';
  if (hours < 1) return `${Math.round(hours * 60)}m`;
  if (hours < 24) return `${hours.toFixed(1)}h`;
  return `${(hours / 24).toFixed(1)}d`;
};

const formatTime = seconds => {
  if (!seconds) return '-';
  if (seconds < 60) return `${Math.round(seconds)}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
  return `${Math.round(seconds / 3600)}h`;
};

const goToConversation = insight => {
  const conversationUrl = `/app/accounts/${route.params.accountId}/conversations/${insight.conversation_id}`;
  router.push(conversationUrl);
};

const hasPrevPage = computed(() => meta.value.current_page > 1);
const hasNextPage = computed(
  () => meta.value.current_page < meta.value.total_pages
);
</script>

<template>
  <div
    class="flex flex-col p-4 rounded-xl bg-n-solid-2 shadow outline-1 outline outline-n-container"
  >
    <h3 class="text-base font-medium text-n-slate-12 mb-4">
      {{ $t('CAPTAIN_ANALYTICS.RECENT_INSIGHTS') }}
    </h3>

    <div v-if="isLoading" class="flex items-center justify-center py-12">
      <Spinner />
    </div>

    <div
      v-else-if="insights.length === 0"
      class="flex items-center justify-center py-12 text-n-slate-11"
    >
      {{ $t('CAPTAIN_ANALYTICS.NO_DATA') }}
    </div>

    <template v-else>
      <div class="overflow-x-auto">
        <table class="w-full text-sm">
          <thead>
            <tr class="border-b border-n-container">
              <th class="text-left py-3 px-2 text-n-slate-11 font-medium">
                {{ $t('CAPTAIN_ANALYTICS.TABLE.CONVERSATION') }}
              </th>
              <th class="text-left py-3 px-2 text-n-slate-11 font-medium">
                {{ $t('CAPTAIN_ANALYTICS.TABLE.INBOX') }}
              </th>
              <th class="text-left py-3 px-2 text-n-slate-11 font-medium">
                {{ $t('CAPTAIN_ANALYTICS.TABLE.SENTIMENT') }}
              </th>
              <th class="text-left py-3 px-2 text-n-slate-11 font-medium">
                {{ $t('CAPTAIN_ANALYTICS.TABLE.LEAD') }}
              </th>
              <th class="text-left py-3 px-2 text-n-slate-11 font-medium">
                {{ $t('CAPTAIN_ANALYTICS.TABLE.TOPICS') }}
              </th>
              <th class="text-left py-3 px-2 text-n-slate-11 font-medium">
                {{ $t('CAPTAIN_ANALYTICS.TABLE.RESPONSE_TIME') }}
              </th>
              <th class="text-left py-3 px-2 text-n-slate-11 font-medium">
                {{ $t('CAPTAIN_ANALYTICS.TABLE.PENDING_BY') }}
              </th>
              <th class="text-left py-3 px-2 text-n-slate-11 font-medium">
                {{ $t('CAPTAIN_ANALYTICS.TABLE.HOURS_PENDING') }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="insight in insights"
              :key="insight.id"
              class="border-b border-n-container hover:bg-n-alpha-1 cursor-pointer"
              @click="goToConversation(insight)"
            >
              <td class="py-3 px-2">
                <span class="text-n-blue-11 font-medium">
                  #{{ insight.conversation_display_id }}
                </span>
              </td>
              <td class="py-3 px-2 text-n-slate-12">
                {{ insight.inbox_name }}
              </td>
              <td class="py-3 px-2">
                <span
                  class="px-2 py-1 rounded-full text-xs font-medium"
                  :class="getSentimentClass(insight.sentiment)"
                >
                  {{ insight.sentiment }}
                </span>
              </td>
              <td class="py-3 px-2">
                <span
                  v-if="insight.is_qualified_lead"
                  class="px-2 py-1 rounded-full text-xs font-medium bg-n-purple-3 text-n-purple-11"
                >
                  {{ $t('CAPTAIN_ANALYTICS.TABLE.QUALIFIED') }}
                </span>
                <span v-else class="text-n-slate-10">-</span>
              </td>
              <td class="py-3 px-2">
                <div class="flex flex-wrap gap-1">
                  <span
                    v-for="topic in (insight.topics || []).slice(0, 3)"
                    :key="topic"
                    class="px-2 py-0.5 rounded bg-n-alpha-2 text-n-slate-11 text-xs"
                  >
                    {{ topic }}
                  </span>
                </div>
              </td>
              <td class="py-3 px-2 text-n-slate-12">
                {{ formatTime(insight.first_response_time) }}
              </td>
              <td class="py-3 px-2">
                <span
                  v-if="insight.pending_by"
                  class="px-2 py-1 rounded-full text-xs font-medium"
                  :class="getPendingClass(insight.pending_by)"
                >
                  {{
                    insight.pending_by === 'agent'
                      ? $t('CAPTAIN_ANALYTICS.PENDING.BY_AGENT')
                      : insight.pending_by === 'customer'
                        ? $t('CAPTAIN_ANALYTICS.PENDING.BY_CUSTOMER')
                        : $t('CAPTAIN_ANALYTICS.PENDING.RESOLVED')
                  }}
                </span>
                <span v-else class="text-n-slate-10">-</span>
              </td>
              <td class="py-3 px-2 text-n-slate-12">
                {{ formatHours(insight.hours_since_last_activity) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div
        class="flex items-center justify-between mt-4 pt-4 border-t border-n-container"
      >
        <span class="text-sm text-n-slate-11">
          {{
            $t('CAPTAIN_ANALYTICS.TABLE.SHOWING', {
              count: insights.length,
              total: meta.total_count,
            })
          }}
        </span>
        <div class="flex gap-2">
          <button
            class="px-3 py-1 rounded border border-n-container text-sm text-n-slate-12 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-n-alpha-1"
            :disabled="!hasPrevPage"
            @click="fetchInsights(meta.current_page - 1)"
          >
            {{ $t('CAPTAIN_ANALYTICS.TABLE.PREV') }}
          </button>
          <button
            class="px-3 py-1 rounded border border-n-container text-sm text-n-slate-12 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-n-alpha-1"
            :disabled="!hasNextPage"
            @click="fetchInsights(meta.current_page + 1)"
          >
            {{ $t('CAPTAIN_ANALYTICS.TABLE.NEXT') }}
          </button>
        </div>
      </div>
    </template>
  </div>
</template>
